// Attach skills to a companion. Attachments live outside the profile document
// (guide_skills join table), so toggles save immediately via the attachment API.

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getGuideSkills, listSkills, setGuideSkills } from "../../services/skills";
import type { SkillWithStats } from "../../types/skill";
import { StatusBadge } from "../StatusBadge";

export function GuideSkillsPicker({
  guideId,
  onError,
}: {
  guideId: string | null;
  onError: (message: string) => void;
}) {
  const [skills, setSkills] = useState<SkillWithStats[]>([]);
  const [attached, setAttached] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!guideId) return;
    setLoading(true);
    try {
      const [allSkills, attachments] = await Promise.all([
        listSkills(),
        getGuideSkills(guideId),
      ]);
      setSkills(allSkills.filter((s) => s.status !== "archived"));
      setAttached(attachments.skillIds);
    } catch (e) {
      onError(`Failed to load skills: ${e instanceof Error ? e.message : e}`);
    } finally {
      setLoading(false);
    }
  }, [guideId, onError]);

  useEffect(() => {
    load();
  }, [load]);

  async function toggle(skillId: string) {
    if (!guideId || saving) return;
    const next = attached.includes(skillId)
      ? attached.filter((s) => s !== skillId)
      : [...attached, skillId];
    setSaving(true);
    const previous = attached;
    setAttached(next);
    try {
      await setGuideSkills(guideId, next);
    } catch (e) {
      setAttached(previous);
      onError(`Failed to update skills: ${e instanceof Error ? e.message : e}`);
    } finally {
      setSaving(false);
    }
  }

  if (!guideId) {
    return <div className="callout">Save the companion first, then attach skills.</div>;
  }
  if (loading) return <div className="state-box">Loading skills…</div>;
  if (skills.length === 0) {
    return (
      <div className="callout">
        No skills exist yet. <Link to="/skills/new">Create a skill</Link> (a topic package
        with a searchable knowledge base), publish it, and attach it here.
      </div>
    );
  }

  return (
    <div className="skill-picker">
      {skills.map((s) => {
        const isAttached = attached.includes(s.id);
        const live = s.status === "published" && s.enabled !== false;
        return (
          <label key={s.id} className={`skill-option${isAttached ? " attached" : ""}`}>
            <input
              type="checkbox"
              checked={isAttached}
              disabled={saving}
              onChange={() => toggle(s.id)}
            />
            <span className="skill-option-name">
              {s.icon ? `${s.icon} ` : ""}
              {s.name || s.id}
            </span>
            <StatusBadge status={s.status} enabled={s.enabled} />
            {s.kind === "protocol" ? (
              <span className="muted">guided exercise</span>
            ) : (
              <span className="muted">
                {s.readyDocumentCount}/{s.documentCount} docs · {s.chunkCount} chunks
              </span>
            )}
            <Link to={`/skills/${s.id}`} className="muted" onClick={(e) => e.stopPropagation()}>
              edit ↗
            </Link>
            {isAttached && !live && (
              <span className="muted"> (draft/disabled — inactive in live sessions)</span>
            )}
          </label>
        );
      })}
      <div className="muted" style={{ marginTop: 8 }}>
        Attached knowledge skills give this companion a search_knowledge tool over the
        selected knowledge bases; attached guided exercises let it run that exercise in
        the conversation. Only published + enabled skills are active in live sessions.
      </div>
    </div>
  );
}
